import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Spinner } from 'react-bootstrap';
import { MessageSquare, Send, Wifi, WifiOff } from 'lucide-react';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import { ROUTE, withQuery } from '../../utils/navigation';
import { useChatSocket } from '../../hooks/useChatSocket';
import './quote-chat.css';

function formatWhen(iso, locale) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const QuoteChatPage = ({ onNavigate, initialThreadId = '', quoteChatEnabled: quoteChatEnabledProp }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(initialThreadId || '');
  const [messages, setMessages] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [quoteChatEnabled, setQuoteChatEnabled] = useState(
    quoteChatEnabledProp !== false,
  );
  const [settingsReady, setSettingsReady] = useState(quoteChatEnabledProp != null);
  const bottomRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getSettingsSection('basic-setup');
        if (!cancelled) {
          setQuoteChatEnabled(data?.basicSetup?.quoteChatEnabled !== false);
        }
      } catch {
        if (!cancelled) setQuoteChatEnabled(true);
      } finally {
        if (!cancelled) setSettingsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadThreads = useCallback(async () => {
    try {
      const data = await api.listChatThreads();
      setThreads(data?.threads || []);
    } catch (err) {
      showToast(err.message || t('chat.toast.loadChatsFailed'), 'error');
      setThreads([]);
    } finally {
      setLoadingThreads(false);
    }
  }, [t]);

  const loadMessages = useCallback(async (threadId) => {
    if (!threadId) {
      setMessages([]);
      setActiveThread(null);
      return;
    }
    setLoadingMessages(true);
    try {
      const data = await api.listChatMessages(threadId);
      setMessages(data?.messages || []);
      setActiveThread(data?.thread || null);
      setThreads((prev) =>
        prev.map((t) => (t.id === threadId ? { ...t, ...(data?.thread || {}), unreadCount: 0 } : t)),
      );
    } catch (err) {
      showToast(err.message || t('chat.toast.loadMessagesFailed'), 'error');
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, [t]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    if (initialThreadId) setActiveId(initialThreadId);
  }, [initialThreadId]);

  useEffect(() => {
    if (activeId) loadMessages(activeId);
  }, [activeId, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const onSocketEvent = useCallback(
    (event) => {
      if (!event?.type) return;
      if (event.type === 'chat.message' && event.payload?.message) {
        const msg = event.payload.message;
        setThreads((prev) => {
          const next = prev.map((t) => {
            if (t.id !== msg.threadId) return t;
            return {
              ...t,
              lastMessageAt: msg.createdAt,
              lastMessagePreview: msg.body,
              unreadCount:
                msg.threadId === activeId || msg.authorType === 'admin'
                  ? t.unreadCount || 0
                  : (t.unreadCount || 0) + 1,
            };
          });
          return [...next].sort((a, b) =>
            String(b.lastMessageAt || b.createdAt).localeCompare(
              String(a.lastMessageAt || a.createdAt),
            ),
          );
        });
        if (msg.threadId === activeId) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
        }
      }
      if (event.type === 'chat.thread' && event.payload?.thread) {
        const thread = event.payload.thread;
        setThreads((prev) => {
          const exists = prev.some((t) => t.id === thread.id);
          const next = exists
            ? prev.map((t) => (t.id === thread.id ? { ...t, ...thread } : t))
            : [thread, ...prev];
          return [...next].sort((a, b) =>
            String(b.lastMessageAt || b.createdAt).localeCompare(
              String(a.lastMessageAt || a.createdAt),
            ),
          );
        });
        if (thread.id === activeId) setActiveThread((t) => ({ ...(t || {}), ...thread }));
      }
    },
    [activeId],
  );

  const { status: socketStatus } = useChatSocket({
    enabled: Boolean(quoteChatEnabled),
    onEvent: onSocketEvent,
  });

  const selectThread = (threadId) => {
    setActiveId(threadId);
    onNavigate?.(withQuery(ROUTE.CHAT, { thread: threadId }));
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || !activeId || sending) return;
    setSending(true);
    try {
      const data = await api.postChatMessage(activeId, body);
      const msg = data?.message;
      if (msg) {
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      }
      setDraft('');
    } catch (err) {
      showToast(err.message || t('chat.toast.sendFailed'), 'error');
    } finally {
      setSending(false);
    }
  };

  const socketLabel = useMemo(() => {
    if (socketStatus === 'open') return t('chat.live');
    if (socketStatus === 'connecting' || socketStatus === 'reconnecting') return t('chat.connecting');
    if (socketStatus === 'error') return t('chat.offline');
    return t('chat.idle');
  }, [socketStatus, t]);

  if (!settingsReady) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!quoteChatEnabled) {
    return (
      <div className="py-3 px-2">
        <div className="mb-4">
          <h2 className="h4 mb-1">{t('chat.title')}</h2>
          <p className="text-muted mb-0">{t('chat.subtitleDisabled')}</p>
        </div>
        <div className="q2c-chat-disabled">
          <MessageSquare size={28} />
          <h3 className="h5 mt-3">{t('chat.disabledTitle')}</h3>
          <p className="text-muted mb-3">
            {t('chat.disabledBody')}
          </p>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onNavigate?.(ROUTE.SETTINGS_BASIC_SETUP)}
          >
            {t('chat.openBasicSetup')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="q2c-chat py-3 px-2">
      <div className="q2c-chat-header mb-3">
        <div>
          <h2 className="h4 mb-1">{t('chat.title')}</h2>
          <p className="text-muted mb-0">{t('chat.subtitleConvos')}</p>
        </div>
        <div className={`q2c-chat-live ${socketStatus === 'open' ? 'is-live' : ''}`}>
          {socketStatus === 'open' ? <Wifi size={14} /> : <WifiOff size={14} />}
          {socketLabel}
        </div>
      </div>

      <div className="q2c-chat-layout">
        <aside className="q2c-chat-inbox">
          <div className="q2c-chat-inbox-head">{t('chat.inbox')}</div>
          {loadingThreads ? (
            <div className="p-4 text-center">
              <Spinner animation="border" size="sm" />
            </div>
          ) : threads.length === 0 ? (
            <div className="q2c-chat-empty-pane">
              <p className="mb-1 fw-semibold">{t('chat.noChatsTitle')}</p>
              <p className="small text-muted mb-0">
                {t('chat.noChatsBody')}
              </p>
            </div>
          ) : (
            <ul className="q2c-chat-thread-list">
              {threads.map((th) => (
                <li key={th.id}>
                  <button
                    type="button"
                    className={`q2c-chat-thread-item${th.id === activeId ? ' is-active' : ''}`}
                    onClick={() => selectThread(th.id)}
                  >
                    <div className="q2c-chat-thread-top">
                      <span className="q2c-chat-thread-title">
                        {th.quoteNumber || t('chat.quote')} · {th.customerName || t('chat.customer')}
                      </span>
                      {(th.unreadCount || 0) > 0 ? (
                        <span className="q2c-chat-unread">{th.unreadCount}</span>
                      ) : null}
                    </div>
                    <div className="q2c-chat-thread-preview">
                      {th.lastMessagePreview || t('chat.noMessages')}
                    </div>
                    <div className="q2c-chat-thread-meta">
                      {formatWhen(th.lastMessageAt || th.createdAt, locale)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="q2c-chat-pane">
          {!activeId ? (
            <div className="q2c-chat-empty-pane">
              <MessageSquare size={28} />
              <p className="mt-2 mb-0 fw-semibold">{t('chat.selectConversation')}</p>
            </div>
          ) : (
            <>
              <div className="q2c-chat-pane-head">
                <div>
                  <div className="fw-semibold">
                    {activeThread?.quoteNumber || t('chat.quote')} —{' '}
                    {activeThread?.customerName || t('chat.customer')}
                  </div>
                  <div className="small text-muted">{activeThread?.customerEmail || ''}</div>
                </div>
                {activeThread?.quoteId ? (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => onNavigate?.(`${ROUTE.QUOTES}/${activeThread.quoteId}`)}
                  >
                    {t('chat.openQuote')}
                  </Button>
                ) : null}
              </div>

              <div className="q2c-chat-messages">
                {loadingMessages ? (
                  <div className="p-4 text-center">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={`q2c-chat-bubble q2c-chat-bubble--${m.authorType}`}
                    >
                      <div className="q2c-chat-bubble-meta">
                        {m.authorType === 'admin'
                          ? t('chat.you')
                          : m.authorType === 'system'
                            ? t('chat.system')
                            : t('chat.customer')}{' '}
                        · {formatWhen(m.createdAt, locale)}
                      </div>
                      <div className="q2c-chat-bubble-body">{m.body}</div>
                    </div>
                  ))
                )}
                <div ref={bottomRef} />
              </div>

              <Form className="q2c-chat-composer" onSubmit={send}>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder={t('chat.composerPlaceholder')}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  disabled={sending}
                />
                <Button type="submit" disabled={sending || !draft.trim()}>
                  <Send size={16} />
                  {t('chat.send')}
                </Button>
              </Form>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default QuoteChatPage;
